import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useSidebar } from '../../context/SidebarContext';
import Bildirimler from './Bildirimler';
import PostaKutusu from './PostaKutusu';
import './Navbar.css';
import { YENI_TASARIM } from '../../utils/yeniTasarim';
import { onayla } from '../../utils/onayla';

const Navbar = ({ onizlemeYolu } = {}) => {
  const { logout, user } = useAuth();
  const { toggleSidebar } = useSidebar();
  const navigate = useNavigate();
  const location = useLocation();
  const isDashboard = location.pathname === '/dashboard' || location.pathname === '/';
  const [showUserMenu, setShowUserMenu] = useState(false);
  const userMenuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
    };

    if (showUserMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showUserMenu]);

  const handleLogout = async () => {
    // Yeni tasarimda cikis once sorulur (canli sistemde dogrudan cikar).
    if (YENI_TASARIM && !(await onayla('Bu cihazda oturumunuz kapanacak. Face ID ile tekrar girebilirsiniz.', {
      baslik: 'Çıkış yapılsın mı?', onay: 'Çıkış yap', tehlikeli: true,
    }))) return;
    logout();
    navigate('/login');
    setShowUserMenu(false);
  };

  const handleProfileClick = () => {
    navigate('/profile');
    setShowUserMenu(false);
  };

  const getUserDisplayName = () => {
    if (!user) return 'Kullanıcı';
    const name = user.Name ?? user.name ?? '';
    const surname = user.Surname ?? user.surname ?? '';
    const fullName = `${name}${surname ? ' ' + surname : ''}`.trim();
    const base = fullName || (user.Username ?? user.UserName ?? 'Kullanıcı');
    // Anasayfada avukat ünvanı kısaltması
    if (isDashboard) {
      const title = user.Title ?? user.title ?? '';
      const isAvukat = title === 'Avukat' || title === 'Lawyer' || title === 'lawyer';
      if (isAvukat) return `Av. ${base}`;
    }
    return base;
  };

  /* Telefonda ust cubukta sayfanin adi gorunur (masaustunde gizli). */
  const sayfaAdi = (() => {
    const p = onizlemeYolu || location.pathname;
    const harita = [
      ['/dashboard', 'Anasayfa'], ['/case-files/', 'Dosya'], ['/case-files', 'Dosyalar'],
      ['/archived-files', 'Arşiv'], ['/calendar', 'Takvim'], ['/job-list', 'İş Listesi'],
      ['/tasks', 'Görevler'], ['/kasa', 'Kasa'], ['/profile', 'Profil'],
      ['/gelen-postalar', 'Gelen Postalar'], ['/masraf-talepleri', 'Masraf Talepleri'],
      ['/masraflar', 'Masraflar'], ['/muvekkiller', 'Müvekkiller'], ['/ayarlar', 'Ayarlar'],
      ['/kisisel-giderler', 'Kişisel Giderler'], ['/notes', 'Notlar'], ['/users', 'Kullanıcılar'],
    ];
    if (p === '/') return 'Anasayfa';
    const bulunan = harita.find(([yol]) => p.startsWith(yol));
    return bulunan ? bulunan[1] : 'Öztürk Hukuk';
  })();

  /* Telefonda uzun ad yerine bas harfler (ornek: "Baran Öztürk" -> "BÖ"). */
  const basHarfler = (() => {
    if (!user) return '•';
    const ad = (user.Name ?? user.name ?? '').trim();
    const soyad = (user.Surname ?? user.surname ?? '').trim();
    const h = `${ad.charAt(0)}${soyad.charAt(0)}`.toLocaleUpperCase('tr-TR');
    return h || (user.Username ?? user.UserName ?? '?').charAt(0).toLocaleUpperCase('tr-TR');
  })();

  return (
    <nav className={`navbar${showUserMenu ? ' menu-acik' : ''}`}>
      <div className="navbar-left">
        <button className="navbar-menu-toggle" onClick={toggleSidebar}>☰</button>
        <Link to="/dashboard" className="navbar-logo" aria-label="Anasayfa"><img src="/assets/logo.png" alt="" /></Link>
        <Link to="/dashboard" className="nav-button home-button">ANASAYFA</Link>
      </div>
      <div className="navbar-center">
        {!isDashboard && (
          <h1 className="firm-name">Öztürk Hukuk ve Danışmanlık</h1>
        )}
        {/* Yalnizca telefonda gorunur (styles/mobil-arayuz.css) */}
        <span className="navbar-sayfa" key={sayfaAdi}>{sayfaAdi}</span>
      </div>
      <div className="navbar-right">
        <Link to="/case-files" className="nav-button files-button">DOSYALAR</Link>
        <Link to="/archived-files" className="nav-button archive-button">ARŞİV</Link>
        <Link to="/calendar" className="nav-button calendar-button">TAKVİM</Link>
        <Link to="/job-list" className="nav-button job-list-button">İŞ LİSTESİ</Link>
        <Link to="/tasks" className="nav-button tasks-button">GÖREVLER</Link>
        {/* Posta kutusu ve bildirim merkezi — kullanici menusunun solunda.
            Postalar canin icinden cikarildi; sayilari cok oldugu icin
            is gerektiren bildirimleri bastiriyorlardi. */}
        <PostaKutusu />
        <Bildirimler />

        <div className="user-info">
          <div className="user-menu-container" ref={userMenuRef}>
            <button 
              className="username-button"
              onClick={() => setShowUserMenu(!showUserMenu)}
            >
              {/* Telefonda adin yerine bas harfler gorunur */}
              {/* Yeni tasarimda bas harf yerine kisi simgesi: Turkce "İ" kucuk
                  puntoda kucuk "i" gibi okunuyordu ("İÖ" -> "iö"). */}
              <span className="navbar-avatar" aria-hidden="true" title={basHarfler}>
                {YENI_TASARIM ? (
                  <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
                    <circle cx="12" cy="8.5" r="4" />
                    <path d="M4.5 20.5c1.2-4 4.2-6 7.5-6s6.3 2 7.5 6" />
                  </svg>
                ) : basHarfler}
              </span>
              <span className="username">{getUserDisplayName()}</span>
              <span className="dropdown-arrow">▼</span>
            </button>
            {showUserMenu && (
              <div className="user-menu-dropdown">
                <button 
                  className="user-menu-item"
                  onClick={handleProfileClick}
                >
                  Profil
                </button>
                <button 
                  className="user-menu-item"
                  onClick={handleLogout}
                >
                  Çıkış Yap
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;